import { useLayoutEffect, useRef, type ReactElement } from 'react';
import { Link, useLocation, useMatch, useParams } from 'react-router';
import { ErrorBoundary } from '../app/ErrorBoundary';
import { isApiError } from '../api';
import { useSession } from '../auth';
import { ChatPane } from '../features/chat';
import { useConversation } from '../features/conversation';
import {
  DETAILS_HEADING_TARGET,
  DETAILS_LINK_TARGET,
  FOCUS_TARGET_ATTRIBUTE,
} from '../features/conversation/focus-targets';
import { DetailsPanel } from '../features/details';
import styles from './ConversationPage.module.css';

/**
 * Mesmo ponto de quebra de `ConversationPage.module.css` (um teste confere que os dois continuam
 * iguais): abaixo dele chat e detalhes viram duas telas, uma por vez.
 */
export const DETAILS_BREAKPOINT_QUERY = '(max-width: 1099px)';

/**
 * Os detalhes abrem e fecham sob demanda, e quem estava focado some junto (o link do nome, ou o painel que fecha): o
 * foco cairia no <body>. Ao abrir, o foco vai ao título dos detalhes; ao fechar, ao nome do cliente de onde a pessoa
 * saiu. Só reage a TRANSIÇÕES (nunca à primeira renderização).
 */
function useViewFocus(view: 'chat' | 'details', root: React.RefObject<HTMLElement | null>): void {
  const previous = useRef<'chat' | 'details' | null>(null);
  useLayoutEffect(() => {
    const before = previous.current;
    previous.current = view;
    if (before === null || before === view) return;
    const target = view === 'details' ? DETAILS_HEADING_TARGET : DETAILS_LINK_TARGET;
    root.current
      ?.querySelector<HTMLElement>(`[${FOCUS_TARGET_ATTRIBUTE}="${target}"]`)
      ?.focus({ preventScroll: true });
  }, [view, root]);
}

/** Rota `/c/:conversationId` e `/c/:conversationId/detalhes`: chat + painel de detalhes. */
export function ConversationPage(): ReactElement {
  const { conversationId = '' } = useParams();
  const { search } = useLocation(); // preserva o filtro da lista ao navegar
  const view = useMatch('/c/:conversationId/detalhes') ? 'details' : 'chat';
  const session = useSession();
  const query = useConversation(conversationId);
  const pageRef = useRef<HTMLDivElement>(null);
  useViewFocus(view, pageRef);

  const listHref = { pathname: '/', search };
  const chatHref = { pathname: `/c/${encodeURIComponent(conversationId)}`, search };
  const detailsHref = { pathname: `/c/${encodeURIComponent(conversationId)}/detalhes`, search };

  if (query.isPending) {
    return (
      <section className={styles.state} role="status">
        <p className={styles.muted}>Carregando conversa…</p>
      </section>
    );
  }

  // A tela cheia de erro é para quando NÃO há o que mostrar (primeira carga) ou a conversa deixou de
  // existir/ser visível (404). Uma atualização em segundo plano que falha (rede oscilando ao voltar
  // para a aba) mantém os dados e a tela como estão: trocar chat e painel por um erro apagaria o
  // que a pessoa está escrevendo.
  const notFound = query.isError && isApiError(query.error) && query.error.status === 404;
  if (query.isError && (query.data === undefined || notFound)) {
    return (
      <section className={styles.state}>
        {notFound ? (
          <>
            <h2 className={styles.heading}>Conversa não encontrada</h2>
            <p className={styles.muted}>
              Ela pode ter sido transferida para outra pessoa ou você não tem acesso a ela.
            </p>
          </>
        ) : (
          <p role="alert" className={styles.error}>
            {isApiError(query.error) && query.error.status >= 500
              ? 'O servidor está indisponível no momento. Tente de novo em instantes.'
              : query.error.message}
          </p>
        )}
        <div className={styles.actions}>
          {!notFound && (
            <button type="button" className={styles.button} onClick={() => void query.refetch()}>
              Tentar de novo
            </button>
          )}
          <Link className={styles.link} to={listHref}>
            Voltar para as conversas
          </Link>
        </div>
      </section>
    );
  }

  if (session.status !== 'authenticated') return <></>; // RequireAuth já garante

  return (
    <div ref={pageRef} className={styles.page} data-view={view}>
      <div className={styles.chat}>
        <ErrorBoundary scope="panel" resetKey={conversationId}>
          <ChatPane
            conversation={query.data}
            listHref={listHref}
            detailsHref={view === 'details' ? chatHref : detailsHref}
            detailsOpen={view === 'details'}
          />
        </ErrorBoundary>
      </div>
      <div className={styles.details}>
        <ErrorBoundary scope="panel" resetKey={conversationId}>
          <DetailsPanel
            conversation={query.data}
            viewer={{ id: session.user.id, role: session.user.role }}
            chatHref={chatHref}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
}
